/*!
  Pioneer WFRIA NavBar v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["NavBar"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("NavBar") < 0) {
    window.__PIONEER_GLOBAL__.components.push("NavBar");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["navBarAriaLabel", "navBarItemsAriaLabel", "customStrings", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import debounce from "lodash.debounce";
import PropTypes from "prop-types";
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { KEYBOARD_KEYS, NAVBARITEM } from "../common/constants";
import { useOnClickOutside } from "../common/hooks";
import { invalidIf } from "../common/proptypes";
import { mergeRefs, safeMerge } from "../common/utilities";
import { useResizeObserver } from "../private/ThirdParty";
import { OVERRIDE } from "../themes/common/classes";
import { RESIZE_DEBOUNCE_TIME_MS } from "./constants";
import extractTheme from "./extractTheme";
import { NavBarContext } from "./NavBarContext";
import STRINGS from "./strings";
import { Button, IconClose, IconMenu } from "..";
/**
 * The nav bar provides a container of links to other pages according to
 * the application's information architecture (IA).
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const NavBar = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    navBarAriaLabel,
    navBarItemsAriaLabel,
    customStrings,
    children
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    navElement,
    itemsRoot,
    item,
    closeButton,
    allSectionsButton,
    dropDownContainer,
    itemsRootDropDown,
    closeButtonContainer,
    allSectionsButtonLI,
    itemsRootNonDropDown
  } = extractTheme(_objectSpread({
    theme
  }, otherProps)); // Merged user passed string with internal strings.

  const componentStrings = _objectSpread(_objectSpread({}, STRINGS), customStrings);

  const {
    allSectionsButtonLabel,
    closeButtonLabel
  } = componentStrings;
  const {
    ESCAPE
  } = KEYBOARD_KEYS; // To hold individual width of each NavBarItem during initial render.

  const navBarWidths = useRef([]); // Hold values of NavBarItems that have MegaMenus.

  const navItemsWithMegaMenus = useRef([]);
  const navBarUlRef = useRef(null);
  const navRef = useRef(null);
  const closeElementRef = useRef(null); // Hold non disabled NavBarItems.

  const activeNavBarItems = useRef([]); // Hold disabled NavBarItem indexes.

  const disabledNavBarItemIndexes = useRef([]); // Holds the active elements of MegaMenu.

  const focusableElements = useRef(null); // To prevent `focus` being applied on component load.

  const focusOnLoad = useRef(false);
  const allSectionsElementRef = useRef(); // A flag to know if component is mounted.

  const instanceMounted = useRef(false);
  /**
   * A state variable to show/hide `AllSections` button.
   * Default set to true to calculate the width of the `AllSections` button on load.
   */

  const [showAllSections, setShowAllSections] = useState(true); // To know if NavBar is in dropdown or normal view.

  const [navItemsDropDown, setNavItemsDropDown] = useState(false); // Holds the count of visible NavBarItems w.r.t available width.

  const [totalVisibleElements, setTotalVisibleElements] = useState(0); // To save `AllSections` button width during initial render.

  const [allSectionButtonWidth, setAllSectionButtonWidth] = useState(0); // Holds the current active NavBarItem.

  const [selectedNavItem, setSelectedNavItem] = useState(""); // Flag for showing & hiding MegaMenu.

  const [megaMenuVisibility, setMegaMenuVisibility] = useState(false); // Holds index of active element in NavBarItem.

  const [activeElement, setActiveElement] = useState(0);
  /**
   * This method is called when a `click` or `mousedown` action is made
   * outside of NavBar component.
   */

  const handleOutsideClick = () => {
    // Closes MegaMenu if it is open.
    if (megaMenuVisibility) {
      setMegaMenuVisibility(false);
    } // If NavBarItems are in dropdown view then change it to normal view.


    if (navItemsDropDown) {
      setNavItemsDropDown(false);
    }
  };
  /**
   * Hook to detect and trigger handler when `click` or `mousedown` action
   * happens outside of `NavBar` component.
   */


  useOnClickOutside(navRef, handleOutsideClick, true); // This handler is called from `AllSections` and `Close` button clicks.

  const handleButtonClick = () => {
    // Changes the NavBar view from between normal and dropdown.
    setNavItemsDropDown(!navItemsDropDown);
  };
  /**
   * This handler is responsible to get current available width
   * for NavBarItems and decide on number of NavBarItems to be shown.
   * Based on the visible NavBarItems we are deciding if `AllSections` button
   * should be displayed.
   */


  const resizeHandler = () => {
    let navBarItemWidths = 0,
        visibleItems = 0,
        visibleItemsWidth = 0; // If in dropdown view all NavBarItems will be visible.

    if (navItemsDropDown) {
      visibleItems = navBarUlRef.current.childNodes.length;
    } else {
      // Width available for NavBarItems to render.
      const containerWidth = navBarUlRef.current.offsetWidth; // Iterating over NavBarItems widths.

      navBarWidths.current.forEach(navBarItem => {
        navBarItemWidths = navBarItemWidths + navBarItem;

        if (containerWidth > navBarItemWidths) {
          visibleItems++;
          visibleItemsWidth = visibleItemsWidth + navBarItem;
        }
      });
      /**
       * Checking if `AllSections` button need to be rendered.
       * If `visibleItems` is less than NavBar items length then we need to
       * show `AllSections` button.
       */

      const showAllSectionsButton = visibleItems < navBarWidths.current.length;

      if (showAllSectionsButton) {
        /**
         * If `AllSections` is rendered it would take up some width in `NavBar`,
         * there might be a scenario where there is not enough width available for
         * `AllSections` button to render below logic is to make sure we have enough
         * width for `AllSections` button.
         * We are freeing up width by removing last visible items and validating if
         * the new width is enough to render `AllSections` button.
         */
        const iterationLength = visibleItems - 1;

        for (let index = iterationLength; index >= 0; index--) {
          if (visibleItemsWidth + allSectionButtonWidth > containerWidth) {
            visibleItems--;
            visibleItemsWidth = visibleItemsWidth - navBarWidths.current[index];
          } else {
            break;
          }
        }
      }

      setShowAllSections(showAllSectionsButton);
    } // Setting the visible items count and `AllSections` button visibility state.


    setTotalVisibleElements(visibleItems);
  };
  /**
   * This layout effect should execute only once during initial render.
   * This is the place where we are capturing width of all NavBarItems and
   * `AllSections` button.
   */


  useLayoutEffect(() => {
    const {
      childNodes
    } = navBarUlRef.current;
    childNodes.forEach((node, index) => {
      /**
       * 40 is the margin added to each list item.
       * As first item do not have margin left making sure not to add the same
       * during first iteration.
       */
      navBarWidths.current.push(node.offsetWidth + (index === 0 ? 0 : 40));
      /**
       * If NavBarItem is `disabled` put the disabled index to
       * `disabledNavBarItemIndexes`.
       */

      if (node.querySelector("[disabled]")) {
        disabledNavBarItemIndexes.current.push(index);
      } else {
        var _node$querySelector;

        // Get non disabled NavBarItem label that has MegaMenu.
        const buttonNodeLabel = (_node$querySelector = node.querySelector("button")) === null || _node$querySelector === void 0 ? void 0 : _node$querySelector.textContent;
        const hasMegaMenu = Boolean(buttonNodeLabel);
        /**
         * Save all NavBarItem labels that has MegaMenu in them else save
         * an empty string.
         */

        navItemsWithMegaMenus.current.push(hasMegaMenu ? buttonNodeLabel : "");
      }
    });
    /**
     * As `showAllSections` is set to `true` on load it would be rendered as
     * last item in the list.
     * Removing the last item from `navBarWidths` and setting the `AllSections`
     * button width to state.
     */

    const allSectionWidth = navBarWidths.current.pop();
    setAllSectionButtonWidth(allSectionWidth); // On load `AllSections` button would also be rendered so removing it.

    navItemsWithMegaMenus.current.pop(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // This effect is expected to execute only when `megaMenuVisibility` change.

  useEffect(() => {
    if (!megaMenuVisibility) {
      var _activeNavBarItems$cu;

      // If MegaMenu is closed due to any action then `focus` current activeElement.
      (_activeNavBarItems$cu = activeNavBarItems.current[activeElement]) === null || _activeNavBarItems$cu === void 0 ? void 0 : _activeNavBarItems$cu.focus();
      /**
       * When ever MegaMenu is closed setting the selected NavBarItem
       * back to `defaultSelected` NavBarItem.
       */

      React.Children.map(children, child => {
        const {
          label,
          defaultSelected
        } = child.props;
        if (defaultSelected) setSelectedNavItem(label);
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [megaMenuVisibility]);
  /**
   * This useEffect is expected to execute whenever visible items change
   * or when view is changed from dropdown to normal or vice versa.
   * This useEffect is to make sure `activeNavBarItems` has latest visible
   * active NavBarItems in DOM.
   */

  useEffect(() => {
    activeNavBarItems.current = [];
    const {
      childNodes
    } = navBarUlRef.current; // Get all non disabled activeElements(NavBarItems).

    focusableElements.current = navBarUlRef.current.querySelectorAll("li a:not([disabled]), li button:not([disabled])");
    childNodes.forEach(node => {
      // `activeNavBarItems` will only have non disabled NavBarItems.
      if (!node.querySelector("[disabled]")) {
        activeNavBarItems.current.push(node.querySelector("button"));
      }
    });
    /**
     * If `showAllSections` is `true` and view is normal(not dropdown) making
     * sure to remove `AllSections` button from `activeNavBarItems`.
     */

    if (showAllSections && !navItemsDropDown) {
      activeNavBarItems.current.pop();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [navItemsDropDown, totalVisibleElements]);
  /**
   * Ideally this `useEffect` will execute once during initial render,
   * when we set `allSectionButtonWidth` in `useLayoutEffect`.
   */

  useEffect(() => {
    /**
     * Condition to prevent `resizeHandler` from being called before
     * `allSectionButtonWidth` is set to state.
     */
    if (allSectionButtonWidth) {
      resizeHandler();
    }
    /**
     * Disabled warning to add `resizeHandler` as dependency as it will make this `useEffect`
     * execute after initial render.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [allSectionButtonWidth]);
  useEffect(() => {
    if (instanceMounted.current) {
      if (!navItemsDropDown) {
        var _allSectionsElementRe;

        (_allSectionsElementRe = allSectionsElementRef.current) === null || _allSectionsElementRe === void 0 ? void 0 : _allSectionsElementRe.focus();
      } else {
        var _closeElementRef$curr;

        (_closeElementRef$curr = closeElementRef.current) === null || _closeElementRef$curr === void 0 ? void 0 : _closeElementRef$curr.focus();
      }
    }

    instanceMounted.current = true;
  }, [navItemsDropDown]);
  const {
    ref: internalContainerRef
  } = useResizeObserver({
    box: "border-box",
    onResize: debounce(resizeHandler, RESIZE_DEBOUNCE_TIME_MS)
  }); // Merged the refs here to invoke the resize handler on window resize.

  const mergedContainerRef = mergeRefs([internalContainerRef, navBarUlRef]);
  useImperativeHandle(ref, () => ({
    resizeHandler
  }));
  /**
   * This method is invoked during keydown event and validates the key pressed.
   * If key pressed is `ESC` then will set the `megaMenuVisibility` to false to close `MegaMenu`.
   * @param {evt} event - Event object.
   */

  const handleNavBarKeyDown = _ref2 => {
    let {
      key
    } = _ref2;

    if (key === ESCAPE && megaMenuVisibility) {
      setMegaMenuVisibility(false);
    }
  };
  /**
   * This effect is used to focus current active element based on `tab` and
   * `arrow` key interactions.
   */


  useEffect(() => {
    // Skip focus on load and when MegaMenu is open.
    if (focusOnLoad.current && !megaMenuVisibility) {
      focusableElements.current[activeElement].focus();
    }

    focusOnLoad.current = true; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeElement]);
  /**
   * This method will return active visible NavBarItems count. Useful to know
   * if the current activeElement is last NavBarItem.
   * @returns {Number} - total visible items excluding disabled NavBarItems.
   */

  const isLastElementActive = () => {
    // If `AllSections` button is rendered.
    if (showAllSections) {
      /**
       * If in dropdown view that means `AllSections` button is clicked.
       * `totalVisibleElements` hold count of NavBarItems rendered, we need to
       * do a `minus 1` as its length and activeElement is `index` based.
       * In dropdown view all NavBarItems render, as we are only interested in
       * non disabled NavBarItems we are removing them from the count.
       */
      if (navItemsDropDown) {
        return totalVisibleElements - 1 - disabledNavBarItemIndexes.current.length;
      } else {
        /**
         * If we are in normal view and `AllSections` button is rendered we
         * need to consider that as an active NavBarItem so no `minus 1` here.
         * `disabledNavBarItemIndexes` hold `disabled` NavBarItems indexes and we
         * only want to consider the count of visible and disabled NavBarItems
         * so `filter` dose that job.
         */
        return totalVisibleElements - disabledNavBarItemIndexes.current.filter(value => value < totalVisibleElements).length;
      }
    } else {
      /**
       * If we have all NavBarItems rendered directly remove disabled count and
       * `minus 1` to match activeElement index.
       */
      return totalVisibleElements - 1 - disabledNavBarItemIndexes.current.length;
    }
  };
  /**
   * This event handler executes during `keyDown` action in NavBarItem.
   * @param {event} event - event object.
   */
  // eslint-disable-next-line complexity


  const handleKeydown = event => {
    // To prevent event handler execution when MegaMenu is open.
    if (!megaMenuVisibility) {
      const {
        TAB,
        UP,
        DOWN,
        LEFT,
        RIGHT
      } = KEYBOARD_KEYS;
      const {
        key,
        shiftKey
      } = event; // Current visible active NavBarItems count in view.

      const activeNavBarItems = isLastElementActive(); // To know if active NavBarItems is last NavBarItem.

      const lastElementActive = activeElement === activeNavBarItems;

      if (key === TAB) {
        /**
         * Preventing default/Keyboard Navigator behavior on `tab` in cases
         * where focus movement is not as per NavBar behavior expectations.
         * In all other cases default/Keyboard Navigator behavior matches
         * with NavBar behavior.
         * Condition explained:
         * - If activeElement is not last NavBarItem and `shift` key not used.
         * - OR
         * - If activeElement is not last NavBarItem and `shift` key is not used
         * on first NavBarItem.
         * - OR
         * - If `shift` key is used on last activeElement.
         */
        if (!lastElementActive && !shiftKey || !lastElementActive && !(activeElement === 0 && shiftKey) || shiftKey && lastElementActive) {
          event.preventDefault();
        }
        /**
         * Increment activeElement by 1.
         * If `shift` key is not used and activeElement is not last element.
         * Decrement activeElement by 1.
         * If `shift` key is used and activeElement is not first element.
         */


        if (!shiftKey && !lastElementActive) {
          setActiveElement(activeElement + 1);
        } else if (shiftKey && activeElement !== 0) {
          setActiveElement(activeElement - 1);
        }
      }

      if (key === DOWN) {
        // To know if current NavBarItem has a MenuMenu.
        const activeNavItem = navItemsWithMegaMenus.current[activeElement];
        /**
         * If MegaMenu is present open MegaMenu on `keydown` otherwise move focus
         * to next NavBarItem if its not last NavBarItem.
         */

        if (activeNavItem) {
          setSelectedNavItem(activeNavItem);
          setMegaMenuVisibility(true);
        } else if (!lastElementActive) {
          setActiveElement(activeElement + 1);
        }
      } else if (key === UP || key === LEFT) {
        /**
         * It activeElement is not first NavBarItem move focus to previous
         * NavBarItem.
         */
        if (activeElement !== 0) {
          setActiveElement(activeElement - 1);
        }
      }

      if (key === RIGHT) {
        if (!lastElementActive) {
          setActiveElement(activeElement + 1);
        }
      }
    }
  };
  /**
   * Iterates over all the children(NavBarItem) and returns
   * the renderable NavBarItems.
   * @returns Array of NavBarItem components.
   */


  const renderNavItems = () => React.Children.map(children, (child, index) => {
    /**
     * If view is dropdown then we need to show all the NavBarItem components.
     * If `totalVisibleElements` is `0` it means we are in initial render phase
     * and want to render all items to get widths of all NavBarItems.
     * If `totalVisibleElements` is greater than current `index` it means we
     * have enough width to show the NavBarItem.
     */
    if (navItemsDropDown || totalVisibleElements === 0 || totalVisibleElements > index) {
      return child;
    }
  });
  /**
   * We are maintaining two different structures for the NavBar component
   * based the type of view.
   * View can be normal view or a dropdown view.
   */


  const navBarItems = () => {
    return navItemsDropDown ? _jsxs("div", {
      css: closeButtonContainer,
      children: [_jsx(Button, {
        fullWidth: true,
        kind: "simple",
        ref: closeElementRef,
        buttonStyles: safeMerge(item, closeButton),
        label: closeButtonLabel,
        leftIcon: _jsx(IconClose, {
          size: "xsmall",
          decorative: true
        }),
        onClick: handleButtonClick,
        "aria-expanded": navItemsDropDown,
        "aria-haspopup": navItemsDropDown
      }), _jsx("div", {
        css: dropDownContainer,
        children: _jsx("ul", {
          "aria-label": navBarItemsAriaLabel,
          css: safeMerge(itemsRoot, itemsRootDropDown),
          ref: mergedContainerRef,
          onKeyDown: handleKeydown,
          className: OVERRIDE,
          children: renderNavItems()
        })
      })]
    }) : _jsxs("ul", {
      "aria-label": navBarItemsAriaLabel,
      css: safeMerge(itemsRoot, itemsRootNonDropDown),
      ref: mergedContainerRef,
      onKeyDown: handleKeydown,
      className: OVERRIDE,
      children: [renderNavItems(), showAllSections && _jsx("li", {
        css: allSectionsButtonLI,
        className: OVERRIDE,
        children: _jsx(Button, {
          kind: "simple",
          ref: allSectionsElementRef,
          buttonStyles: safeMerge(item, allSectionsButton),
          label: allSectionsButtonLabel,
          leftIcon: _jsx(IconMenu, {
            size: "xsmall"
          }),
          onClick: handleButtonClick,
          "aria-expanded": navItemsDropDown,
          "aria-haspopup": navItemsDropDown
        })
      })]
    });
  }; // Props to pass to children components.


  const childProps = {
    navItemsDropDown,
    selectedNavItem,
    // To let MegaMenu know its last NavBarItem.
    lastNavBarItem: activeElement === isLastElementActive(),
    // Handler to hold selected NavBarItem.
    handleSelectedNavItem: navItemName => {
      setSelectedNavItem(navItemName);
      /**
       * When a click event is triggered on `NavBarItem` `activeIndex`
       * needs to be set to the respective `NavBarItem` to sync browser
       * `focus` with `NavBar`.
       */

      if (focusableElements.current) {
        focusableElements.current.forEach((element, index) => {
          if (element.textContent === navItemName) setActiveElement(index);
        });
      }
    },
    // Handler to move focus from current NavBarItem to next NavBarItem.
    handleKeyboardActions: () => {
      setActiveElement(activeElement + 1);
    },
    // Holds MegaMenu open/close state.
    showMegaMenu: megaMenuVisibility,
    // Handler to open/close MegaMenu from children.
    setShowMegaMenu: setMegaMenuVisibility
  };
  return _jsx(NavBarContext.Provider, {
    value: childProps,
    children: _jsx("div", _objectSpread(_objectSpread({
      css: cssRoot,
      onKeyDown: handleNavBarKeyDown
    }, otherProps), {}, {
      children: _jsx("nav", {
        css: navElement,
        "aria-label": navBarAriaLabel,
        ref: navRef,
        children: navBarItems()
      })
    }))
  });
});
NavBar.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Label that describes about the region and to differentiate between other region elements on the page.
   * It is announced when any NavBar item receives focus.
   */
  navBarAriaLabel: PropTypes.string.isRequired,

  /**
   * Label that describes about the list items(eg: main categories).
   * It is announced once after the region description is read.
   */
  navBarItemsAriaLabel: PropTypes.string.isRequired,

  /**
   * These are rendered inside NavBar component.
   * Should pass `NavBarItem` only as children.
   */
  // eslint-disable-next-line react/require-default-props
  children: invalidIf(PropTypes.node.isRequired, (props, propName) => {
    let error = false;
    const prop = props[propName];
    React.Children.map(prop, child => {
      const childrenType = child.type;

      if (childrenType.displayName !== NAVBARITEM) {
        error = true;
      }
    });
    return error;
  }, `'NavBar' children should be of type '${NAVBARITEM}'.`),
  // Override for strings used within the component.
  customStrings: PropTypes.shape({
    /**
     * Label to be shown in `AllSections` button.
     * Defaults to `All Sections`
     */
    allSectionsButtonLabel: PropTypes.string,

    /**
     * Label to shown in `Close` button.
     * Defaults to `Close`.
     */
    closeButtonLabel: PropTypes.string
  })
} : {};
NavBar.defaultProps = {
  customStrings: null
};
NavBar.displayName = "NavBar";
export default NavBar;